import { useNavigate } from 'react-router';
import { ArrowLeft, Shield, FileText, Lock, Cookie, Handshake } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// SHARED LAYOUT
// ═══════════════════════════════════════════════════════════════

function LegalLayout({ title, icon: Icon, lastUpdated, children }: {
  title: string;
  icon: React.ElementType;
  lastUpdated: string;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <div style={{ backgroundColor: 'var(--background)', minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        borderBottom: '1px solid var(--border)',
        backgroundColor: 'var(--card)',
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/landing')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '8px',
              fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)', padding: 0,
            }}
          >
            <ArrowLeft size={18} />
            Voltar
          </button>
        </div>
      </header>

      {/* Content */}
      <main style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px 96px' }}>
        {/* Title block */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '56px', height: '56px', borderRadius: 'var(--radius-card)',
            backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
            marginBottom: '24px',
          }}>
            <Icon size={28} style={{ color: 'var(--primary)' }} />
          </div>
          <h1 style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'clamp(var(--text-xl), 3vw, var(--text-3xl))',
            fontWeight: 'var(--font-weight-bold)', color: 'var(--foreground)',
            marginBottom: '12px', letterSpacing: '-0.5px',
          }}>
            {title}
          </h1>
          <p style={{
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
            color: 'var(--muted-foreground)',
          }}>
            Ultima atualizacao: {lastUpdated}
          </p>
        </div>

        {/* Document body */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
          {children}
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 style={{
        fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)',
        fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)',
        marginBottom: '16px', paddingBottom: '12px',
        borderBottom: '1px solid var(--border)',
      }}>
        {title}
      </h2>
      <div style={{
        fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)',
        color: 'var(--foreground)', lineHeight: 1.8,
        display: 'flex', flexDirection: 'column', gap: '12px',
      }}>
        {children}
      </div>
    </section>
  );
}

function Highlight({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '16px 20px', borderRadius: 'var(--radius-card)',
      backgroundColor: 'color-mix(in srgb, var(--primary) 6%, transparent)',
      border: '1px solid color-mix(in srgb, var(--primary) 15%, transparent)',
      fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
      color: 'var(--foreground)', lineHeight: 1.7,
    }}>
      {children}
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul style={{ paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {items.map((item, i) => (
        <li key={i} style={{
          fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
          color: 'var(--foreground)', lineHeight: 1.7,
        }}>
          {item}
        </li>
      ))}
    </ul>
  );
}

// ═══════════════════════════════════════════════════════════════
// 1. POLITICA DE PRIVACIDADE
// ═══════════════════════════════════════════════════════════════

export function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Politica de Privacidade" icon={Shield} lastUpdated="01 de marco de 2026">
      <Highlight>
        A SimulaDesk Tecnologia Ltda. ("SimulaDesk", "nos") esta comprometida com a protecao dos dados pessoais dos seus usuarios. Esta politica descreve como coletamos, utilizamos, armazenamos e protegemos suas informacoes.
      </Highlight>

      <Section title="1. Dados que Coletamos">
        <p>Coletamos as seguintes categorias de dados pessoais:</p>
        <BulletList items={[
          'Dados de identificacao: nome, e-mail corporativo, cargo e setor.',
          'Dados de uso: gravacoes de sessoes de treino por voz, transcricoes, scores de auditoria e metricas de desempenho.',
          'Dados tecnicos: endereco IP, tipo de navegador, sistema operacional, logs de acesso e cookies.',
          'Dados hierarquicos: vinculacao a supervisor, setor, monitor (humano ou IA) e tenant.',
        ]} />
      </Section>

      <Section title="2. Finalidade do Tratamento">
        <p>Utilizamos seus dados para:</p>
        <BulletList items={[
          'Operar a plataforma de treinamento e auditoria com IA.',
          'Gerar scores, relatorios de desempenho e certificados.',
          'Aplicar o sistema "Falha Grave Zera" em avaliacoes.',
          'Gamificacao: rankings, ligas, badges e metas.',
          'Comunicacoes operacionais e notificacoes do sistema.',
          'Melhorias continuas na plataforma via analytics agregados.',
        ]} />
      </Section>

      <Section title="3. Base Legal">
        <p>O tratamento de dados pessoais e realizado com base em:</p>
        <BulletList items={[
          'Execucao de contrato (Art. 7, V da LGPD).',
          'Consentimento do titular (Art. 7, I da LGPD).',
          'Interesse legitimo do controlador (Art. 7, IX da LGPD).',
        ]} />
      </Section>

      <Section title="4. Compartilhamento de Dados">
        <p>Seus dados podem ser compartilhados com:</p>
        <BulletList items={[
          'Supervisores e gestores de qualidade do seu tenant, conforme hierarquia configurada.',
          'Provedores de infraestrutura (Supabase, servicos de nuvem) sob contratos de confidencialidade.',
          'Autoridades competentes, quando exigido por lei.',
        ]} />
        <p>Nunca comercializamos dados pessoais a terceiros.</p>
      </Section>

      <Section title="5. Retencao e Exclusao">
        <p>Os dados sao retidos pelo periodo necessario a prestacao do servico e cumprimento de obrigacoes legais. Apos o termino do contrato, os dados sao anonimizados ou excluidos em ate 90 dias, salvo obrigacao legal em contrario.</p>
      </Section>

      <Section title="6. Seus Direitos">
        <p>Conforme a LGPD, voce pode solicitar:</p>
        <BulletList items={[
          'Confirmacao e acesso aos dados tratados.',
          'Correcao de dados incompletos ou desatualizados.',
          'Anonimizacao, bloqueio ou eliminacao de dados desnecessarios.',
          'Portabilidade dos dados a outro fornecedor.',
          'Revogacao do consentimento.',
        ]} />
        <Highlight>
          Para exercer seus direitos, envie um e-mail para <strong>privacidade@simuladesk.com.br</strong> com o assunto "Direitos LGPD".
        </Highlight>
      </Section>

      <Section title="7. Seguranca">
        <p>Adotamos medidas tecnicas e organizacionais para proteger seus dados, incluindo criptografia em transito e em repouso, controle de acesso baseado em perfis (RBAC com 5 niveis), Row-Level Security (RLS) no banco de dados e auditorias periodicas de seguranca.</p>
      </Section>

      <Section title="8. Contato do Encarregado (DPO)">
        <p>Encarregado de Protecao de Dados: dpo@simuladesk.com.br</p>
      </Section>
    </LegalLayout>
  );
}

// ═══════════════════════════════════════════════════════════════
// 2. TERMOS DE USO
// ═══════════════════════════════════════════════════════════════

export function TermsOfUsePage() {
  return (
    <LegalLayout title="Termos de Uso" icon={FileText} lastUpdated="01 de marco de 2026">
      <Highlight>
        Ao acessar e utilizar a plataforma SimulaDesk, voce concorda com os termos e condicoes descritos abaixo. Leia atentamente antes de utilizar o servico.
      </Highlight>

      <Section title="1. Definicoes">
        <BulletList items={[
          '"Plataforma": o sistema SimulaDesk de treinamento e auditoria de atendimento com IA.',
          '"Usuario": qualquer pessoa que acesse a plataforma, incluindo operadores, supervisores, monitores e administradores.',
          '"Tenant": a organizacao contratante do servico.',
          '"Conteudo": todo material gerado na plataforma, incluindo gravacoes, transcricoes, avaliacoes e relatorios.',
        ]} />
      </Section>

      <Section title="2. Aceitacao dos Termos">
        <p>O uso da plataforma implica aceitacao integral destes Termos. Caso nao concorde, nao utilize o servico. O acesso e condicionado ao vinculo com um Tenant ativo e a atribuicao de um perfil autorizado (superadmin, admin, qualidade, supervisor ou operador).</p>
      </Section>

      <Section title="3. Uso Permitido">
        <p>A plataforma deve ser utilizada exclusivamente para:</p>
        <BulletList items={[
          'Treinamento de atendimento por voz e texto com simulador de IA.',
          'Auditoria de chamadas reais com questionarios vinculados.',
          'Gestao de qualidade, metas, gamificacao e certificacoes.',
          'Analise de desempenho de operadores e equipes.',
        ]} />
      </Section>

      <Section title="4. Restricoes de Uso">
        <p>E expressamente proibido:</p>
        <BulletList items={[
          'Compartilhar credenciais de acesso com terceiros.',
          'Tentar acessar dados de outros tenants ou burlar o Row-Level Security.',
          'Utilizar a plataforma para fins ilegais ou discriminatorios.',
          'Fazer engenharia reversa, scraping ou tentativas de extracao massiva de dados.',
          'Manipular scores, auditorias ou resultados de gamificacao.',
        ]} />
      </Section>

      <Section title="5. Propriedade Intelectual">
        <p>Todo o conteudo, codigo, design e funcionalidades da plataforma SimulaDesk sao propriedade exclusiva da SimulaDesk Tecnologia Ltda. Os dados gerados pelo Usuario (gravacoes, scores, certificados) sao de propriedade do Tenant contratante, conforme contrato de prestacao de servicos.</p>
      </Section>

      <Section title="6. Disponibilidade e SLA">
        <p>A SimulaDesk se compromete a manter disponibilidade conforme os niveis de servico definidos no documento de SLA. Manutencoes programadas serao comunicadas com antecedencia minima de 48 horas.</p>
      </Section>

      <Section title="7. Limitacao de Responsabilidade">
        <p>A SimulaDesk nao se responsabiliza por danos indiretos, incidentais ou consequenciais decorrentes do uso da plataforma. Os scores e avaliacoes gerados pela IA sao auxiliares e devem ser validados por monitores humanos conforme o fluxo de aprovacao da plataforma.</p>
      </Section>

      <Section title="8. Rescisao">
        <p>O acesso pode ser revogado a qualquer momento pelo administrador do Tenant ou pela SimulaDesk em caso de violacao destes Termos. Apos a rescisao, os dados serao tratados conforme a Politica de Privacidade.</p>
      </Section>

      <Section title="9. Foro">
        <p>Fica eleito o foro da comarca de Sao Paulo/SP para dirimir quaisquer controversias decorrentes destes Termos, com exclusao de qualquer outro, por mais privilegiado que seja.</p>
      </Section>
    </LegalLayout>
  );
}

// ═══════════════════════════════════════════════════════════════
// 3. CONFORMIDADE LGPD
// ═══════════════════════════════════════════════════════════════

export function LGPDPage() {
  return (
    <LegalLayout title="Conformidade LGPD" icon={Lock} lastUpdated="01 de marco de 2026">
      <Highlight>
        A SimulaDesk esta em conformidade com a Lei Geral de Protecao de Dados Pessoais (Lei n. 13.709/2018). Este documento detalha nosso compromisso e as medidas implementadas.
      </Highlight>

      <Section title="1. Compromisso com a LGPD">
        <p>A SimulaDesk adota uma abordagem de Privacy by Design e Privacy by Default em todas as funcionalidades da plataforma. O tratamento de dados e minimizado ao estritamente necessario para a operacao do servico.</p>
      </Section>

      <Section title="2. Papeis e Responsabilidades">
        <BulletList items={[
          'Controlador: o Tenant (empresa contratante) que define as finalidades do tratamento.',
          'Operador de dados: a SimulaDesk Tecnologia Ltda., que realiza o tratamento em nome do Tenant.',
          'Encarregado (DPO): profissional designado para comunicacao com titulares e ANPD.',
          'Sub-operadores: provedores de infraestrutura (Supabase) atuam como sub-operadores sob contrato.',
        ]} />
      </Section>

      <Section title="3. Medidas Tecnicas Implementadas">
        <BulletList items={[
          'Row-Level Security (RLS): isolamento completo de dados entre tenants no banco de dados.',
          'RBAC com 5 perfis: superadmin, admin, qualidade, supervisor, operador — cada um com permissoes especificas.',
          'Criptografia TLS 1.3 em transito e AES-256 em repouso.',
          'Logs de auditoria imutaveis com registro de todas as acoes criticas.',
          'Backups automaticos com retencao configuravel.',
          'Autenticacao segura com suporte a MFA.',
        ]} />
      </Section>

      <Section title="4. Integridade Referencial de Dados">
        <p>O modelo de dados do SimulaDesk garante integridade referencial total com 38 tabelas, 4 views e 65+ indexes. Toda auditoria e vinculada obrigatoriamente a questionario, operador, supervisor, setor e monitor (humano ou IA), garantindo rastreabilidade completa.</p>
      </Section>

      <Section title="5. Direitos dos Titulares">
        <p>A plataforma oferece mecanismos para que os titulares (operadores e usuarios) exercam seus direitos previstos nos artigos 17 a 22 da LGPD:</p>
        <BulletList items={[
          'Acesso aos dados pessoais tratados.',
          'Correcao de dados incompletos ou inexatos.',
          'Eliminacao de dados tratados com consentimento.',
          'Portabilidade a outro fornecedor.',
          'Informacao sobre compartilhamento com terceiros.',
          'Revogacao do consentimento.',
        ]} />
      </Section>

      <Section title="6. Resposta a Incidentes">
        <p>Em caso de incidente de seguranca que envolva dados pessoais, a SimulaDesk notificara o Tenant controlador e a ANPD em ate 72 horas, conforme Art. 48 da LGPD, com detalhamento da natureza dos dados afetados, titulares envolvidos e medidas corretivas adotadas.</p>
      </Section>

      <Section title="7. Transferencia Internacional">
        <p>Dados podem ser processados em servidores fora do Brasil (infraestrutura Supabase). A transferencia internacional e realizada conforme Art. 33 da LGPD, com clausulas contratuais padrao e garantias de nivel adequado de protecao.</p>
      </Section>

      <Section title="8. Contato">
        <Highlight>
          <strong>Encarregado de Protecao de Dados (DPO):</strong> dpo@simuladesk.com.br<br />
          <strong>ANPD:</strong> www.gov.br/anpd
        </Highlight>
      </Section>
    </LegalLayout>
  );
}

// ═══════════════════════════════════════════════════════════════
// 4. POLITICA DE COOKIES
// ═══════════════════════════════════════════════════════════════

export function CookiePolicyPage() {
  return (
    <LegalLayout title="Politica de Cookies" icon={Cookie} lastUpdated="01 de marco de 2026">
      <Highlight>
        A SimulaDesk utiliza cookies e tecnologias similares para garantir o funcionamento adequado da plataforma e melhorar a experiencia do usuario.
      </Highlight>

      <Section title="1. O que sao Cookies">
        <p>Cookies sao pequenos arquivos de texto armazenados no seu navegador quando voce acessa a plataforma. Eles nos ajudam a reconhecer seu dispositivo, manter sua sessao ativa e entender como voce utiliza o servico.</p>
      </Section>

      <Section title="2. Tipos de Cookies Utilizados">
        <div style={{
          display: 'grid', gap: '16px',
        }}>
          {[
            { type: 'Essenciais', desc: 'Necessarios para o funcionamento basico da plataforma. Incluem cookies de autenticacao, sessao e CSRF. Nao podem ser desativados.', required: true },
            { type: 'Funcionais', desc: 'Armazenam preferencias como tema (claro/escuro), idioma e configuracoes de dashboard personalizavel.', required: false },
            { type: 'Analiticos', desc: 'Coletam informacoes anonimizadas sobre como a plataforma e utilizada, ajudando a melhorar a experiencia (tempo de sessao, paginas visitadas).', required: false },
            { type: 'De Desempenho', desc: 'Monitoram a performance da plataforma, tempos de carregamento e erros tecnicos para garantir estabilidade.', required: false },
          ].map((cookie) => (
            <div key={cookie.type} style={{
              padding: '16px 20px', borderRadius: 'var(--radius-card)',
              backgroundColor: 'var(--card)', border: '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <h4 style={{
                  fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)',
                }}>
                  {cookie.type}
                </h4>
                <span style={{
                  fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: cookie.required ? 'var(--warning)' : 'var(--success)',
                  backgroundColor: cookie.required
                    ? 'color-mix(in srgb, var(--warning) 10%, transparent)'
                    : 'color-mix(in srgb, var(--success) 10%, transparent)',
                  padding: '2px 10px', borderRadius: 'var(--radius)',
                }}>
                  {cookie.required ? 'Obrigatorio' : 'Opcional'}
                </span>
              </div>
              <p style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                color: 'var(--muted-foreground)', lineHeight: 1.7,
              }}>
                {cookie.desc}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="3. Cookies de Terceiros">
        <p>A plataforma pode utilizar cookies de provedores de infraestrutura (Supabase Auth) para gerenciamento de sessao e autenticacao. Esses cookies sao regidos pelas politicas de privacidade dos respectivos provedores.</p>
      </Section>

      <Section title="4. Como Gerenciar Cookies">
        <p>Voce pode gerenciar cookies atraves das configuracoes do seu navegador. Note que desabilitar cookies essenciais impedira o funcionamento da plataforma. Para cookies funcionais e analiticos, voce pode ajustar suas preferencias nas Configuracoes da plataforma.</p>
      </Section>

      <Section title="5. Retencao">
        <BulletList items={[
          'Cookies de sessao: expiram ao fechar o navegador.',
          'Cookies persistentes: retidos por ate 12 meses.',
          'Cookies analiticos: dados anonimizados retidos por ate 24 meses.',
        ]} />
      </Section>
    </LegalLayout>
  );
}

// ═══════════════════════════════════════════════════════════════
// 5. SLA - ACORDO DE NIVEL DE SERVICO
// ═══════════════════════════════════════════════════════════════

export function SLAPage() {
  return (
    <LegalLayout title="SLA - Acordo de Nivel de Servico" icon={Handshake} lastUpdated="01 de marco de 2026">
      <Highlight>
        Este documento define os niveis de servico garantidos pela SimulaDesk para todos os planos contratados. O SLA e aplicavel a partir da ativacao do tenant na plataforma.
      </Highlight>

      <Section title="1. Disponibilidade">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {[
            { plan: 'Starter', sla: '99.0%', support: 'E-mail' },
            { plan: 'Professional', sla: '99.5%', support: 'E-mail + Chat' },
            { plan: 'Enterprise', sla: '99.9%', support: 'Dedicado 24/7' },
          ].map((tier) => (
            <div key={tier.plan} style={{
              padding: '20px', borderRadius: 'var(--radius-card)',
              backgroundColor: 'var(--card)', border: '1px solid var(--border)',
              textAlign: 'center',
            }}>
              <p style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
                color: 'var(--muted-foreground)', marginBottom: '8px',
              }}>
                {tier.plan}
              </p>
              <p style={{
                fontFamily: 'var(--font-family-mono)', fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-weight-bold)', color: 'var(--primary)',
                marginBottom: '8px',
              }}>
                {tier.sla}
              </p>
              <p style={{
                fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-xs)',
                color: 'var(--muted-foreground)',
              }}>
                Suporte: {tier.support}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="2. Janela de Manutencao">
        <p>Manutencoes programadas sao realizadas entre 02:00 e 06:00 (horario de Brasilia), com notificacao previa de 48 horas via e-mail e notificacao na plataforma. Manutencoes programadas nao sao contabilizadas como indisponibilidade.</p>
      </Section>

      <Section title="3. Tempos de Resposta">
        <div style={{
          overflow: 'auto', borderRadius: 'var(--radius-card)',
          border: '1px solid var(--border)',
        }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-sm)',
          }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--muted)' }}>
                {['Severidade', 'Descricao', 'Resposta', 'Resolucao'].map((h) => (
                  <th key={h} style={{
                    padding: '12px 16px', textAlign: 'left',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--foreground)', borderBottom: '1px solid var(--border)',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { sev: 'Critica (S1)', desc: 'Plataforma indisponivel', resp: '30 min', res: '4 horas' },
                { sev: 'Alta (S2)', desc: 'Funcionalidade critica degradada', resp: '2 horas', res: '8 horas' },
                { sev: 'Media (S3)', desc: 'Funcionalidade nao-critica afetada', resp: '8 horas', res: '48 horas' },
                { sev: 'Baixa (S4)', desc: 'Duvida ou melhoria', resp: '24 horas', res: '5 dias uteis' },
              ].map((row) => (
                <tr key={row.sev} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 'var(--font-weight-medium)', color: 'var(--foreground)' }}>{row.sev}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--muted-foreground)' }}>{row.desc}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--foreground)', fontFamily: 'var(--font-family-mono)' }}>{row.resp}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--foreground)', fontFamily: 'var(--font-family-mono)' }}>{row.res}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="4. Creditos por Indisponibilidade">
        <p>Caso os niveis de disponibilidade nao sejam atingidos em um mes calendario, o Tenant tera direito a creditos proporcionais:</p>
        <BulletList items={[
          'Disponibilidade entre 99.0% e 98.0%: credito de 10% do valor mensal.',
          'Disponibilidade entre 98.0% e 95.0%: credito de 25% do valor mensal.',
          'Disponibilidade abaixo de 95.0%: credito de 50% do valor mensal.',
        ]} />
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
          Creditos devem ser solicitados em ate 30 dias apos o periodo de indisponibilidade.
        </p>
      </Section>

      <Section title="5. Exclusoes">
        <p>O SLA nao se aplica em casos de:</p>
        <BulletList items={[
          'Forca maior (desastres naturais, pandemias, conflitos).',
          'Falhas na infraestrutura de terceiros (provedores de internet do cliente).',
          'Uso indevido ou nao autorizado da plataforma.',
          'Manutencoes programadas dentro da janela definida.',
          'Ataques DDoS ou eventos de seguranca fora do controle da SimulaDesk.',
        ]} />
      </Section>

      <Section title="6. Monitoramento">
        <p>A disponibilidade e monitorada em tempo real. O status da plataforma pode ser consultado em status.simuladesk.com.br. Incidentes sao comunicados via e-mail e notificacao in-app.</p>
      </Section>
    </LegalLayout>
  );
}
